import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, ZoomControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./FFGS.css";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";

// const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// const apiEndpoints = {
//   "FFG": "https://drr-dss-backend.rimes.int/flood/SAOFFGS/ffgs-obs-ffg/",
//   "ASM": "https://drr-dss-backend.rimes.int/flood/SAOFFGS/ffgs-obs-asm/",
//   "Merged Map": "https://drr-dss-backend.rimes.int/flood/SAOFFGS/ffgs-obs-merged-map/",
//   "BMKG Percipitation": "https://drr-dss-backend.rimes.int/flood/SAOFFGS/forecast/ffgs-fcst-bmkg-map/",
//   "BMKG FFFT": "https://drr-dss-backend.rimes.int/flood/SAOFFGS/forecast/ffgs-fcst-bmkg-fft/"
// };

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || "https://drr-dss-backend.rimes.int";
const apiEndpoints = {
    "FFG":                `${backendUrl}/flood/SAOFFGS/ffgs-obs-ffg/`,
    "ASM":                `${backendUrl}/flood/SAOFFGS/ffgs-obs-asm/`,
    "Merged Map":         `${backendUrl}/flood/SAOFFGS/ffgs-obs-merged-map/`,
    "BMKG Percipitation": `${backendUrl}/flood/SAOFFGS/forecast/ffgs-fcst-bmkg-map/`,
    "BMKG FFFT":          `${backendUrl}/flood/SAOFFGS/forecast/ffgs-fcst-bmkg-fft/`
};


const MapControlDropdowns = ({ activeTab, selectedModel, setSelectedModel, selectedTime, setSelectedTime, selectedDate, setSelectedDate, dataHour, setDataHour }) => {
    //   const modelOptions = useMemo(() => (
    //     activeTab === "Observed" ? ["Merged Map", "ASM", "FFG"] : ["BMKG Percipitation","BMKG FFFT"]
    //   ), [activeTab]);
    const modelOptions = useMemo(() => {
        if (activeTab === "Observed") return ["Merged Map", "ASM", "FFG"];
        // FFG (rainfall) is also offered here, since the Observed/Forecast
        // toggle is hidden and this is the only dropdown users see.
        if (activeTab === "Forecast") return ["BMKG Percipitation", "BMKG FFFT", "FFG"];
        return []; // fallback
    }, [activeTab]);

    useEffect(() => {
        if (!modelOptions.includes(selectedModel)) {
            setSelectedModel(modelOptions[0]);
        }
    }, [modelOptions, selectedModel, setSelectedModel]);

  const timeOptions = useMemo(() => {
    if (activeTab === "Observed") {
      if (selectedModel === "Merged Map") return ["01", "03", "06", "24"];
      if (selectedModel === "ASM") return ["06"];
      if (selectedModel === "FFG") return ["01", "03", "06"];
      return [];
    } else if (activeTab === "Forecast") {
      //return ["03", "06", "24"];
      if (selectedModel === "BMKG Percipitation") return ["06", "24"];
      if (selectedModel === "BMKG FFFT") return ["06"];
      if (selectedModel === "FFG") return ["01", "03", "06"];
      return [];
    } else {
      return [];
    }
  }, [activeTab, selectedModel]);

  useEffect(() => {
    if (!timeOptions.includes(selectedTime)) {
      setSelectedTime(timeOptions[0]);
    }
  }, [timeOptions, selectedTime, setSelectedTime]);

  useEffect(() => {
    if (activeTab === "Observed" && !selectedDate) {
      const today = new Date().toISOString().split("T")[0];
      setSelectedDate(today);
    }
  }, [activeTab, selectedDate, setSelectedDate]);

    useEffect(() => {
        const fetchLatestDataHour = async () => {
            const endpoint = apiEndpoints[selectedModel];
            if (!endpoint || !selectedTime || dataHour) return; // prevent override if already set
            const dateParam = activeTab === "Observed" ? selectedDate : new Date().toISOString().split("T")[0];
            try {
                const res = await fetch(`${endpoint}?date=${dateParam}&interval_hours=${selectedTime}`); // no data_hour param
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) {
                    const latestHour = data[0].data_hour;
                    setDataHour(latestHour);
                }
            } catch (error) {
                console.error("Failed to fetch default data hour:", error);
            }
        };
        fetchLatestDataHour();
    }, [selectedModel, selectedTime, selectedDate, activeTab, dataHour, setDataHour]);

  return (
    <div style={{
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
        border: "1px solid #e8ecf1",
        marginBottom: "12px",
    }}>
      <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "12px",
      }}>
        {/* Filter Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
        </div>

        {/* Divider */}
        <div style={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

        {/* Observed Date Picker */}
        {activeTab === "Observed" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label htmlFor="date-picker" style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>Date:</label>
            <input
                id="date-picker"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                style={{ padding: "6px 10px", fontSize: "13px", borderRadius: "6px", border: "1px solid #ccc", minWidth: "140px" }}
            />
          </div>
        )}
        {/* Forecast Date Display (read-only) */}
        {activeTab === "Forecast" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>Forecast Date:</span>
            <span style={{
                padding: "6px 10px",
                fontSize: "13px",
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
                color: "#555",
                minWidth: "140px",
                display: "inline-block",
            }}>
              {new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric"
              })}
            </span>
          </div>
        )}

        {/* Divider */}
        <div style={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

        {/* Model Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label htmlFor="model-select" style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>Model:</label>
          <select id="model-select" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}
              style={{ padding: "6px 10px", fontSize: "13px", borderRadius: "6px", border: "1px solid #ccc", minWidth: "140px" }}>
            {modelOptions.map((model) => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </div>

        {/* Divider */}
        <div style={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

        {/* Time Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label htmlFor="time-select" style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>Data Hour:</label>
          <select id="time-select" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}
              style={{ padding: "6px 10px", fontSize: "13px", borderRadius: "6px", border: "1px solid #ccc", minWidth: "100px" }}>
            {timeOptions.map((time) => (
              <option key={time} value={time}>{time} Hour</option>
            ))}
          </select>
        </div>

        {/* Divider */}
        <div style={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

        {/* Quick Hour Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>Time (UTC):</span>
          <div style={{
            display: "flex",
            backgroundColor: "#f0f2f5",
            borderRadius: "8px",
            padding: "3px",
            gap: "3px",
          }}>
            {["0000", "0600", "1200", "1800"].map((hour) => {
              const label = `${hour.slice(0, 2)}:00`;
              const isActive = dataHour === hour;
              return (
                <button
                  key={hour}
                  onClick={() => setDataHour(hour)}
                  style={{
                    padding: "6px 14px",
                    fontSize: "12px",
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: "0.3px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: isActive ? "#0d6efd" : "transparent",
                    color: isActive ? "#ffffff" : "#555",
                    cursor: "pointer",
                    boxShadow: isActive ? "0 2px 8px rgba(13,110,253,0.25)" : "none",
                    transition: "all 0.2s ease",
                    outline: "none",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

//export default MapControlDropdowns;


const Overview = () => {
    const [boundaryData, setBoundaryData] = useState(null);
    const [tlsBasin, setTlsBasin] = useState(null);
    // Default landing view stays BMKG Percipitation — FFG is now an added
    // choice in the same dropdown, not a change to what loads first.
    const [selectedModel, setSelectedModel] = useState("BMKG Percipitation");
    const [selectedTime, setSelectedTime] = useState("01");
    const [activeTab, setActiveTab] = useState("Forecast");
    const [apiData, setApiData] = useState([]);
    const [hoverContent, setHoverContent] = useState(null);
    const [dataHour, setDataHour] = useState("");
    const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [loading, setLoading] = useState(false);

    const URL_TileMap = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json")
            .then((res) => res.json())
            .then(setBoundaryData)
            .catch(err => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/ffgs_basin_tls.json")
            .then((res) => res.json())
            .then(setTlsBasin)
            .catch(err => console.error("TLS Basin load error:", err));
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const url = apiEndpoints[selectedModel];
            if (!url || !selectedTime || !dataHour) return;

            const dateParam = activeTab === "Observed" ? selectedDate : new Date().toISOString().split("T")[0];

            try {
                setLoading(true);
                const res = await fetch(`${url}?date=${dateParam}&interval_hours=${selectedTime}&data_hour=${dataHour}`);
                const data = await res.json();

                if (Array.isArray(data) && data.length > 0) {
                    setApiData(data);
                } else {
                    console.warn("No data returned for this combination");
                    setApiData([]);
                }
            } catch (err) {
                console.error("API fetch error:", err);
                setApiData([]);
            } finally {
                setLoading(false);
            }
        };

        if (selectedModel && selectedTime && dataHour) {
            fetchData();
        }
    }, [selectedModel, selectedTime, dataHour, activeTab, selectedDate]);


    const getColorFromValue = (value, model, time) => {
      
        if (model === "BMKG FFFT") {
            if (isNaN(value) || value === null || value === undefined) return "#dededf";
            if (value > 40) return "#e62d3a";     
            if (value > 20 && value <= 40) return "#f39b3a";  
            if (value >= 0.1 && value <= 20) return "#f3eb12"; 
            return "#dededf";                     
        }

      
        const colorRanges = colorRangesMap[model]?.[time];
        if (!colorRanges || isNaN(value)) return "#FFFFFF";
        for (let range of colorRanges) {
            const label = range.label;
            let min = 0, max = Infinity;
            if (label.includes(" - ")) {
                const [minStr, maxStr] = label.split(" - ");
                min = parseFloat(minStr);
                max = maxStr.includes("+") ? Infinity : parseFloat(maxStr);
            } else {
                min = parseFloat(label);
                max = min + 0.0001;
            }
            if (value >= min && value < max) return range.color;
        }
        return "#FFFFFF";
    };
    const colorRangesMap = {
        // FFG is rainfall data, same as BMKG Precipitation, so it is shown
        // with that same legend (identical colours and thresholds) rather
        // than a scale of its own.
        FFG: {
            "01": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "03": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "06": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
        },
        ASM: {
            "06": [
            { color: "#2001aa", label: "0.95 - 1" },
            { color: "#30b719", label: "0.90 - 0.95" },
            { color: "#08a803", label: "0.85 - 0.90" },
            { color: "#91ef84", label: "0.65 - 0.85" },
            { color: "#c7b801", label: "0.30 - 0.65" },
            { color: "#f1ef6c", label: "0.01 - 0.30" },
            { color: "white", label: "N/A" },
            ]
        },
        "Merged Map": {
            "01": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "03": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "06": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "24": [
                { color: "#cc00cc", label: "75 - 120+" },
                { color: "#ca0001", label: "50 - 75" },
                { color: "#c9c902", label: "35 - 50" },
                { color: "#03d300", label: "20 - 35" },
                { color: "#2a01dd", label: "10 - 20" },
                { color: "#25d4d3", label: "0.01 - 10" },
                { color: "white", label: "N/A" },
            ],
        },
        "BMKG Percipitation": {
            "03": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "06": [
                { color: "#cc00cc", label: "60 - 90+" },
                { color: "#ca0001", label: "40 - 60" },
                { color: "#c9c902", label: "20 - 40" },
                { color: "#03d300", label: "10 - 20" },
                { color: "#2a01dd", label: "5 - 10" },
                { color: "#25d4d3", label: "0.01 - 5" },
                { color: "white", label: "N/A" },
            ],
            "24": [
                { color: "#cc00cc", label: "75 - 120+" },
                { color: "#ca0001", label: "50 - 75" },
                { color: "#c9c902", label: "35 - 50" },
                { color: "#03d300", label: "20 - 35" },
                { color: "#2a01dd", label: "10 - 20" },
                { color: "#25d4d3", label: "0.01 - 10" },
                { color: "white", label: "N/A" },
            ],
        },
        "BMKG FFFT": {
            // "03": [
            //     { color: "#E30000", label: "60 - 100" },
            //     { color: "#AF0000", label: "40 - 60" },
            //     { color: "#EB7600", label: "25 - 40" },
            //     { color: "#CB6500", label: "10 - 25" },
            //     { color: "#DEDE00", label: "5 - 10" },
            //     { color: "#AEAE00", label: "0.01 - 5" },
            //     { color: "white", label: "N/A" },
            // ],
            // "06": [
            //     { color: "#E30000", label: "60 - 100" },
            //     { color: "#AF0000", label: "40 - 60" },
            //     { color: "#EB7600", label: "25 - 40" },
            //     { color: "#CB6500", label: "10 - 25" },
            //     { color: "#DEDE00", label: "5 - 10" },
            //     { color: "#AEAE00", label: "0.01 - 5" },
            //     { color: "white", label: "N/A" },
            // ],
             "03": [
                { color: "#dededf", label: "No Threat (0/ NA)" },
                { color: "#f3eb12", label: "Less Likely (0.1 - 20)" },
                { color: "#f39b3a", label: "Likely (20 - 40)" },
                { color: "#e62d3a", label: "High Likely (> 40)" },
            ],
            "06": [
                { color: "#dededf", label: "No Threat (0/ NA)" },
                { color: "#f3eb12", label: "Less Likely (0.1 - 20)" },
                { color: "#f39b3a", label: "Likely (20 - 40)" },
                { color: "#e62d3a", label: "High Likely (> 40)" },
            ],
        }
    };
    // FFG (and ASM / Merged Map) are observed tables and only ever carry an
    // `obs_data` column; only the BMKG tables carry `fcst`. So which field
    // holds the value depends on the selected model, not on which tab is
    // showing (FFG is reachable from the Forecast dropdown too).
    const OBS_DATA_MODELS = ["FFG", "ASM", "Merged Map"];
    const getModelValueField = (model) => (OBS_DATA_MODELS.includes(model) ? "obs_data" : "fcst");

    const getBasinStyle = (feature) => {
        const match = apiData.find(item => Number(item.basin_id) === feature.properties.VALUE);
        const valueRaw = match?.[getModelValueField(selectedModel)];
        const value = parseFloat(valueRaw);
        return {
            color: "#333",
            weight: 0.5,
            opacity: 1,
            fillColor: getColorFromValue(value, selectedModel, selectedTime),
            fillOpacity: 1
        };
    };

    const BoxedLegend = ({ title, colorRanges }) => (
        <MapLegendControl
            sections={[{
                title,
                shape: "square",
                items: colorRanges.map((item) => ({ label: item.label, color: item.color })),
            }]}
        />
    );

    // const getLegendTitle = () => selectedModel === "ASM" ? "Fraction" : `mm / ${selectedTime} hr`;
    const getLegendTitle = () => selectedModel === "BMKG FFFT" ? "" : `mm / ${selectedTime} hr`;

    const getThreatLabel = (val) => {
        if (isNaN(val) || val === null || val === undefined) return "No Threat";
        if (val > 40) return "High Likely";
        if (val > 20 && val <= 40) return "Likely";
        if (val >= 0.1 && val <= 20) return "Less Likely";
        return "No Threat";
    };

    const onEachFeature = (feature, layer) => {
        const match = apiData.find(item => Number(item.basin_id) === feature.properties.VALUE);
        const rawValue = parseFloat(match?.[getModelValueField(selectedModel)]);
        const value = !isNaN(rawValue) ? `${rawValue.toFixed(2)} mm` : "N/A";

        const hoverText = selectedModel === "BMKG FFFT"
            ? `<strong>Basin Details</strong><p>ID: ${feature.properties.VALUE}<br/>Threat: ${getThreatLabel(rawValue)}</p>`
            : `<strong>Basin Details</strong><p>ID: ${feature.properties.VALUE}<br/>Value: ${value}</p>`;

        layer.on({
            mouseover: () => setHoverContent(hoverText),
            mouseout: () => setHoverContent(null)
        });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedModel("FFG");
        setSelectedTime("01");
    };


  const tabButtonStyle = (isActive) => ({
    backgroundColor: isActive ? '#0d6efd' : '#ffffff',
    border: '1px solid #e9ecef',
    color: isActive ? '#fff' : '#0d6efd',
    padding: '0.25rem 0.75rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  });

  return (
    <div className="map-container-ffgs-flood">
        <div className="map-tab-toggle styled-tab">
            {/* <button className={`tab-btn ${activeTab === "Observed" ? "active" : ""}`} onClick={() => handleTabChange("Observed")}>Observed</button>
            <button className={`tab-btn ${activeTab === "Forecast" ? "active" : ""}`} onClick={() => handleTabChange("Forecast")}>Forecast</button> */}
            <div
                style={{
                display: 'none',
                gap: '0.5rem',
                marginBottom: '1rem',
                
                padding: '0.5rem',
                borderRadius: '4px',
                }}
            >
                <button style={tabButtonStyle(activeTab === "Observed")} onClick={() => handleTabChange("Observed")}>
                    Observed
                </button>
                <button style={tabButtonStyle(activeTab === "Forecast")} onClick={() => handleTabChange("Forecast")}>
                    Forecast
                </button>
            </div>
        </div>
        <MapControlDropdowns
            activeTab={activeTab}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            dataHour={dataHour}
            setDataHour={setDataHour}
        />
        <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden" }}>
        <MapContainer center={[-8.7950, 125.6877]} zoom={9} zoomControl={false} attributionControl={false} style={{ width: "100%", height: "calc(100vh - 280px)" }}>
          <MapHomeControl />
          <MapInfoControl text="Flash Flood Guidance System (FFGS) products showing flash flood threat and guidance values by basin." />
            <TileLayer url={URL_TileMap} />
            <ZoomControl position="topleft" />
            <LayersControl position="topright">
                
                <LayersControl.Overlay name="Timor-Leste">
                    {boundaryData && <GeoJSON data={boundaryData} interactive={false} style={{ color: "#ff0000", weight: 1, fillOpacity: 0 }} />}
                </LayersControl.Overlay>
                <LayersControl.Overlay name="Basin" checked>
                    {tlsBasin && apiData.length > 0 && (
                    <GeoJSON
                        key={`${selectedModel}-${selectedTime}-${JSON.stringify(apiData)}`}
                        data={tlsBasin}
                        style={getBasinStyle}
                        onEachFeature={onEachFeature}
                    />
                    )}
                </LayersControl.Overlay>
            </LayersControl>
            <BoxedLegend title={getLegendTitle()} colorRanges={colorRangesMap[selectedModel]?.[selectedTime] || []} />
        </MapContainer>
        </div>
        {/* {renderHourSlider(dataHour, setDataHour)} */}
        {!loading && apiData.length === 0 && (
            <div className="no-data-msg">
                No data available for the selected model, time, and hour.
            </div>
        )}
        {hoverContent && <div className="hover-box-ffgs" dangerouslySetInnerHTML={{ __html: hoverContent }} />}
        {loading && (
        <div className="loading-overlay">
            <img src="/loader.webp" alt="Loading..." className="custom-loader" />
        </div>
        )}
    </div>
  );
};
export default Overview;
